import { useState } from "react";
import { useNavigate, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user && user !== false) return <Navigate to={loc.state?.from || "/moodscape"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      navigate(loc.state?.from || "/moodscape", { replace: true });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="h-9 w-9 rounded-full bg-[var(--primary)] flex items-center justify-center">
            <Heart size={18} className="text-white" />
          </div>
          <span className="font-serif-display text-3xl text-[var(--primary)]">Feelique</span>
        </div>

        <div className="bg-white rounded-[1.75rem] border border-[var(--border)] p-7 sm:p-9 shadow-[0_20px_60px_-30px_rgba(107,79,158,0.25)]">
          <h1 className="font-serif-display text-3xl text-[var(--primary)] leading-tight mb-2">
            {mode === "login" ? "Welcome back." : "Start your Moodscape."}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mb-7">
            {mode === "login" ? "Pick up your mood journal where you left off." : "Free to use. Takes a minute to set up."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <Field label="Your name">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="auth-input"
                  placeholder="What should we call you?"
                />
              </Field>
            )}
            <Field label="Email">
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
                placeholder="you@example.com"
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="auth-input"
                placeholder="At least 6 characters"
              />
            </Field>

            {err && (
              <div className="rounded-2xl bg-[#FBEDE7] border border-[#E9C2B3] text-[#7a3320] px-4 py-2.5 text-sm">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[var(--primary)] text-white text-base font-medium hover:bg-[var(--primary-hover)] transition-all duration-300 focus:ring-4 focus:ring-[var(--primary)]/20 disabled:opacity-60"
            >
              {busy ? "…" : (<>{mode === "login" ? "Sign in" : "Create account"}<ArrowRight size={16} /></>)}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-[var(--border)] text-center text-sm text-[var(--text-muted)]">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              type="button"
              onClick={() => { setMode(mode === "login" ? "signup" : "login"); setErr(""); }}
              className="text-[var(--primary)] font-medium hover:underline"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
      <style>{`.auth-input{width:100%;border-radius:9999px;border:1px solid var(--input-border);background:#fff;padding:.85rem 1.25rem;font-size:.95rem;color:var(--text-main);outline:none;transition:all .2s}.auth-input::placeholder{color:#aaa}.auth-input:focus{border-color:var(--primary);box-shadow:0 0 0 4px rgba(107,79,158,.12)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
